<template>
  <div
    class="app-card-glass rounded-lg flex flex-col h-full border-r border-[var(--p-surface-200)] dark:border-[var(--p-surface-700)]"
  >
    <!-- Header principal -->
    <div class="px-2 py-1.5 mt-2">
      <div class="flex items-center justify-between mb-1.5">
        <h1
          class="text-sm font-semibold text-[var(--p-primary-color)] dark:text-[var(--p-surface-0)] tracking-tight"
        >
          {{ $t('whatsapp.chats') }}
        </h1>

        <div class="flex items-center gap-1">
          <!-- Orden de llegada: ascendente o descendente -->
          <Button
            rounded
            severity="secondary"
            text
            v-tooltip.top="
              sortDirection === 'desc'
                ? $t('whatsapp.chat_list.sort_newest_first')
                : $t('whatsapp.chat_list.sort_oldest_first')
            "
            @click="chatStore.toggleSortDirection(activeTab)"
            class="w-8 h-8 dark:hover:!bg-[var(--p-primary-900)]/30 hover:scale-105"
          >
            <template #icon>
              <component
                :is="sortDirection === 'desc' ? ArrowDownWideNarrow : ArrowUpNarrowWide"
                :size="16"
                class="text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)]"
              />
            </template>
          </Button>

          <!-- Botón nueva conversación -->
          <Button
            rounded
            severity="secondary"
            text
            v-tooltip.top="$t('whatsapp.actions.start_conversation')"
            @click="$emit('open-template-dialog')"
            class="w-8 h-8 dark:hover:!bg-[var(--p-primary-900)]/30 hover:scale-105"
          >
            <template #icon>
              <SendHorizontal
                :size="16"
                class="text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)]"
              />
            </template>
          </Button>

          <!-- Botón toggle filtros -->
          <div class="relative">
            <Button
              rounded
              severity="secondary"
              text
              v-tooltip.top="
                showFilters
                  ? $t('whatsapp.chat_list.hide_filters')
                  : $t('whatsapp.chat_list.show_filters')
              "
              @click="toggleFilters"
              class="w-8 h-8 transition-colors"
              :class="
                showFilters
                  ? 'bg-[var(--p-primary-50)] dark:hover:!bg-[var(--p-primary-900)]/30 scale-105'
                  : 'dark:hover:!bg-[var(--p-primary-900)]/30'
              "
            >
              <template #icon>
                <SlidersHorizontal
                  :size="16"
                  class="transition-colors duration-200"
                  :class="
                    showFilters
                      ? 'text-[var(--p-primary-600)] dark:text-[var(--p-primary-400)]'
                      : 'text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)]'
                  "
                />
              </template>
            </Button>
            <!-- Punto indicador de filtros activos -->
            <span
              v-if="hasActiveFilters"
              class="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-[var(--p-primary-color)] border-2 border-[var(--p-surface-0)] pointer-events-none"
            />
          </div>
        </div>
      </div>

      <!-- Panel de filtros colapsable -->
      <Transition name="filters-slide">
        <div v-if="showFilters" class="overflow-hidden pt-2 pb-2 px-1">
          <AppSelect
            v-model="selectedCampaignId"
            :options="campaignOptions"
            :label="$t('whatsapp.campaings')"
            :placeholder="$t('general.all_campaings')"
            class="mb-2"
            :show-clear="true"
            :editable="true"
            :disabled="campaignOptions.length === 0"
          >
            <template #icon>
              <IconCampaign
                width="14"
                height="14"
                class="text-[var(--p-primary-color)] dark:text-[var(--p-primary-200)]"
              />
            </template>
          </AppSelect>

          <AppInput
            v-model="searchQuery"
            :placeholder="$t('whatsapp.chat_list.search_chat')"
            size="small"
          >
            <template #icon>
              <Search
                :size="14"
                class="text-[var(--p-primary-color)] dark:text-[var(--p-primary-200)]"
              />
            </template>
          </AppInput>
        </div>
      </Transition>

      <!-- Resumen de filtros activos (visible cuando está colapsado) -->
      <Transition name="filters-slide">
        <div v-if="!showFilters && hasActiveFilters" class="flex flex-wrap gap-1 mt-1">
          <span
            v-if="selectedCampaignId"
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border border-[var(--p-primary-50)]/80 bg-[var(--p-surface-0)] text-[var(--p-primary-color)] dark:text-[var(--p-primary-200)] dark:border-[var(--p-primary-color)]/40 dark:bg-[var(--p-primary-800)]/40 cursor-pointer hover:bg-[var(--p-surface-200)]/80 dark:hover:bg-[var(--p-primary-700)]/60 transition-colors shadow-xs"
            @click="toggleFilters"
          >
            <IconCampaign width="10" height="10" />
            {{
              campaignOptions.find((o) => o.value === selectedCampaignId)?.name ??
              $t('whatsapp.chat_list.campaign')
            }}
          </span>
          <span
            v-if="searchQuery.trim()"
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border border-surface-200/80 bg-surface-100 text-surface-700 dark:border-surface-700 dark:bg-surface-800 dark:text-surface-300 cursor-pointer hover:bg-surface-200/80 dark:hover:bg-surface-700 transition-colors shadow-xs"
            @click="toggleFilters"
          >
            <Search :size="10" />
            "{{ searchQuery }}"
          </span>
        </div>
      </Transition>
    </div>

    <!-- Tabs de tipo de chat -->
    <div class="px-1 py-1 mb-1">
      <div class="grid grid-cols-2 2xl:grid-cols-3 gap-2 mx-2">
        <button
          v-for="option in chatTypeOptions"
          :key="option.value"
          type="button"
          class="cursor-pointer montserrat font-semibold flex min-h-6 w-full items-center justify-between rounded-lg border px-2 py-1 text-xs transition-colors duration-150 shadow-xs hover:shadow-sm"
          :class="
            activeTab === option.value
              ? 'border-[var(--p-primary-color)]! bg-[var(--p-primary-color)]! text-[var(--p-primary-contrast-color)]!'
              : ''
          "
          :style="activeTab === option.value ? {} : { color: option.color }"
          :aria-pressed="activeTab === option.value"
          @click="activeTab = option.value"
        >
          <span class="truncate">{{ option.label }}</span>
          <span class="text-[11px] font-medium opacity-90">({{ option.count }})</span>
        </button>
      </div>
    </div>

    <!-- Estado vacío -->
    <div
      v-if="filteredChats.length === 0"
      class="flex-1 flex items-center justify-center px-3 py-4"
    >
      <div class="text-center">
        <MessageCircle
          :size="48"
          class="text-[var(--p-surface-400)]/50 dark:text-[var(--p-surface-200)] mb-3 mx-auto"
        />
        <p class="text-xs text-[var(--p-surface-500)]/50 dark:text-[var(--p-surface-200)]">
          {{
            searchQuery
              ? $t('whatsapp.chat_list.no_chats_found')
              : $t('whatsapp.chat_list.no_chats_available')
          }}
        </p>
      </div>
    </div>

    <!-- Lista de chats -->
    <div
      v-else
      v-bind="containerProps"
      class="flex-1 overflow-y-auto"
      @scroll.passive="handleVirtualScroll"
    >
      <div v-bind="wrapperProps">
        <div
          v-for="{ data: chat } in virtualChats"
          :key="chat.chat_id"
          class="w-full"
          :style="{ height: `${CHAT_ITEM_HEIGHT}px` }"
        >
          <ChatListItem
            :contact-name="chat.contact_name"
            :contact-profile-name="chat.contact_profile_name"
            :contact-username="chat.contact_username"
            :last-message="getChatPreview(chat)"
            :last-message-time="chat.last_message_time"
            :unread-count="chat.unread_count"
            :is-selected="selectedChatId === chat.chat_id"
            :message-status="getLastMessageStatus(chat)"
            :waiting-time="getChatWaitingTime(chat)"
            :campaign-name="chat.campaign_name"
            :chat-status="chat.status"
            :channel-id="chat.channel_id"
            :is-my-expired-chat="
              chat.status === ChatStatus.EXPIRE && chat.user_id === currentUserId
            "
            @select="$emit('select-chat', chat.chat_id)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useVirtualList } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import {
  ArrowDownWideNarrow,
  ArrowUpNarrowWide,
  MessageCircle,
  Search,
  SendHorizontal,
  SlidersHorizontal,
} from 'lucide-vue-next'
import AppInput from '@/components/atoms/inputs/AppInput.vue'
import AppSelect from '@/components/atoms/selects/AppSelect.vue'
import type { SelectOption } from '@/components/atoms/selects/types/select-option.types'
import IconCampaign from '@/assets/svg/campaign_icon.svg?component'
import ChatListItem from '../../molecules/chat-list/ChatListItem.vue'
import type { Chat } from '../../types/interfaces'
import type { MessageStatus } from '../../types/enums'
import { ChatStatus, MessageSourceType } from '../../types/enums'
import { isMyChat, isQueuedChat, useChatStore } from '../../stores/useChatStore'
import { CHAT_FILTER_COLORS, MY_CHATS_TAB_ID } from '../../services/constants'
import { getLastVisibleMessage, isAwaitingAgentReply } from '../../services/messageVisibility'
import { useTimeFormatter } from '../../composables/useTimeFormatter'
import { useTemplateService } from '../../services/useTemplateService'
import { getMessagePreviewText } from '../../services/messageContentFormatter'
import Button from 'primevue/button'
import { useI18n } from 'vue-i18n'

interface Props {
  chats: Chat[]
  selectedChatId: string | null
  currentUserId: string | null
  /** Pestana pedida desde fuera (los chips de la barra de campanas). */
  requestedTab?: string | null
}

const props = defineProps<Props>()
defineEmits<{
  'select-chat': [chatId: string]
  'open-template-dialog': []
}>()

const chatStore = useChatStore()
const { sortDirection } = storeToRefs(chatStore)

const { calculateWaitingTime } = useTimeFormatter()
const { getTemplateById, getTemplateByName } = useTemplateService()
const { t } = useI18n()

const searchQuery = ref('')
const showFilters = ref(false)

type ChatTypeFilter = ChatStatus | typeof MY_CHATS_TAB_ID
interface ChatTypeOptionItem {
  label: string
  value: ChatTypeFilter
  color: string
}

const activeTab = ref<ChatTypeFilter>(MY_CHATS_TAB_ID)
const selectedCampaignId = ref<string | null>(null)

// Toggle del panel de filtros
const toggleFilters = () => {
  showFilters.value = !showFilters.value
}

// Indica si hay algún filtro activo
const hasActiveFilters = computed(
  () => !!selectedCampaignId.value || searchQuery.value.trim().length > 0,
)

// Forzar re-renderizado para actualizar tiempos
const refreshKey = ref(0)
let refreshInterval: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  refreshInterval = setInterval(() => {
    refreshKey.value++
  }, 30000)
})

onUnmounted(() => {
  if (refreshInterval) clearInterval(refreshInterval)
})

const chatTypeOptionItems = computed<ChatTypeOptionItem[]>(() => [
  {
    label: t('whatsapp.chat_list.my_chats'),
    value: MY_CHATS_TAB_ID,
    color: 'color-mix(in srgb, var(--p-primary-color) 50%, transparent)',
  },
  {
    label: t('whatsapp.chat_list.queued'),
    value: ChatStatus.PENDING,
    color: CHAT_FILTER_COLORS.QUEUED,
  },
  {
    label: t('whatsapp.chat_list.to_reply'),
    value: ChatStatus.ATTENTION,
    color: CHAT_FILTER_COLORS.PENDING_REPLY,
  },
  {
    label: t('whatsapp.chat_list.abandoned'),
    value: ChatStatus.ABANDON,
    color: CHAT_FILTER_COLORS.ABANDONED,
  },
  {
    label: t('whatsapp.chat_list.expired'),
    value: ChatStatus.EXPIRE,
    color: CHAT_FILTER_COLORS.EXPIRED,
  },
  {
    label: t('whatsapp.chat_list.templates'),
    value: ChatStatus.TEMPLATE,
    color: CHAT_FILTER_COLORS.TEMPLATES,
  },
])

const chatTypeOptions = computed(() =>
  chatTypeOptionItems.value.map((option) => ({
    ...option,
    count:
      option.value === MY_CHATS_TAB_ID
        ? props.chats.filter(isAssignedChat).length
        : option.value === ChatStatus.PENDING
          ? props.chats.filter(isPendingChat).length
          : option.value === ChatStatus.ATTENTION
            ? props.chats.filter(isAttentionChatPendingReply).length
            : props.chats.filter((chat) => chat.status === option.value).length,
  })),
)

const currentTabChats = computed(() => {
  if (activeTab.value === MY_CHATS_TAB_ID) return props.chats.filter(isAssignedChat)
  if (activeTab.value === ChatStatus.PENDING) return props.chats.filter(isPendingChat)
  if (activeTab.value === ChatStatus.ATTENTION)
    return props.chats.filter(isAttentionChatPendingReply)
  return props.chats.filter((chat) => chat.status === activeTab.value)
})

// Mismo predicado que la insignia de no leidos (useChatStore): si la pestana y
// el contador se definen aparte, tarde o temprano dicen cosas distintas.
const isAssignedChat = (chat: Chat) => isMyChat(chat)
const isPendingChat = (chat: Chat) => isQueuedChat(chat)
// Un chat esta por responder si es mio y lo ultimo que se dijo lo dijo el
// cliente. Antes solo miraba ATENTION —el estado dura hasta la primera
// respuesta del agente, despues el chat pasa a IN_PROGRESS y no volvia nunca a
// esta pestana— y tomaba el ultimo mensaje del array sin filtrar, asi que una
// nota de sistema encima (transferencia, entrada de un agente) lo escondia.
const isAttentionChatPendingReply = (chat: Chat) =>
  isMyChat(chat) && isAwaitingAgentReply(chat.messages)

// La pestana se pide de fuera con un valor y se suelta enseguida (vuelve a
// null), asi que pedir la misma dos veces seguidas vuelve a moverla.
watch(
  () => props.requestedTab,
  (tab) => {
    if (!tab) return
    if (!chatTypeOptionItems.value.some((option) => option.value === tab)) return

    activeTab.value = tab as ChatTypeFilter
  },
  { immediate: true },
)

const campaignOptions = computed<SelectOption[]>(() => {
  const campaigns = new Map<string, string>()
  currentTabChats.value.forEach((chat) => {
    if (!chat.campaign_id || campaigns.has(chat.campaign_id)) return
    campaigns.set(chat.campaign_id, chat.campaign_name || t('whatsapp.chat_list.unnamed_campaign'))
  })
  return Array.from(campaigns.entries())
    .map(([value, name]) => ({ name, value }))
    .sort((a, b) => a.name.localeCompare(b.name))
})

watch(campaignOptions, (options) => {
  if (!selectedCampaignId.value) return
  if (!options.some((o) => o.value === selectedCampaignId.value)) {
    selectedCampaignId.value = null
  }
})

const filteredChats = computed(() => {
  const chats = selectedCampaignId.value
    ? currentTabChats.value.filter((chat) => chat.campaign_id === selectedCampaignId.value)
    : currentTabChats.value

  if (!searchQuery.value.trim()) return chats

  const query = searchQuery.value.toLowerCase()
  return chats.filter(
    (chat) =>
      chat.contact_name.toLowerCase().includes(query) ||
      chat.contact_phone.toLowerCase().includes(query) ||
      (chat.contact_username?.toLowerCase().includes(query.replace(/^@/, '')) ?? false) ||
      getChatPreview(chat).toLowerCase().includes(query),
  )
})

// ------------------------ LISTA VIRTUAL DE CHATS ----------------------------------------
const INITIAL_CHAT_LIMIT = 30
const CHAT_LOAD_STEP = 20

// Ajusta este valor según la altura real de ChatListItem.
// Debe coincidir lo mejor posible con la altura visual del item.
const CHAT_ITEM_HEIGHT = 80

const loadedChatCount = ref(INITIAL_CHAT_LIMIT)

const visibleChats = computed(() => {
  return filteredChats.value.slice(0, loadedChatCount.value)
})

const hasMoreChatsToLoad = computed(() => {
  return loadedChatCount.value < filteredChats.value.length
})

const loadMoreChats = () => {
  if (!hasMoreChatsToLoad.value) return

  loadedChatCount.value = Math.min(
    loadedChatCount.value + CHAT_LOAD_STEP,
    filteredChats.value.length,
  )
}

const {
  list: virtualChats,
  containerProps,
  wrapperProps,
  scrollTo,
} = useVirtualList(visibleChats, {
  itemHeight: CHAT_ITEM_HEIGHT,
  overscan: 8,
})

const handleVirtualScroll = (event: Event) => {
  const target = event.currentTarget as HTMLElement
  const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight

  if (distanceToBottom <= CHAT_ITEM_HEIGHT * 3) {
    loadMoreChats()
  }
}

// Cuando cambien filtros o tab, reinicia la lista
watch(
  activeTab,
  (tab) => {
    chatStore.setActiveListTab(tab)
  },
  { immediate: true },
)

watch([activeTab, selectedCampaignId, searchQuery], async () => {
  loadedChatCount.value = INITIAL_CHAT_LIMIT

  await nextTick()
  scrollTo(0)
})

// Si por alguna actualización externa quedan menos chats,
// evita que loadedChatCount quede por encima del total.
watch(
  () => filteredChats.value.length,
  (total) => {
    if (loadedChatCount.value > total) {
      loadedChatCount.value = Math.max(INITIAL_CHAT_LIMIT, total)
    }
  },
)

//--------------------------------------------------------------------------------------------

const getChatPreview = (chat: Chat): string => {
  if (chatStore.isChatTyping(chat.chat_id)) return t('whatsapp.chat_header.typing')
  const lastMessage = chat.messages?.[chat.messages.length - 1]
  if (!lastMessage)
    return typeof chat.last_message === 'string'
      ? chat.last_message
      : t('whatsapp.chat_list.no_messages')
  const preview =
    getMessagePreviewText(lastMessage.content, getTemplateById, getTemplateByName) ||
    (typeof chat.last_message === 'string' ? chat.last_message : null) ||
    t('whatsapp.chat_list.no_messages')
  return typeof preview === 'string' ? preview : t('whatsapp.chat_list.no_messages')
}

const getChatWaitingTime = (chat: Chat) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _ = refreshKey.value
  const lastMessage = getLastVisibleMessage(chat.messages)
  if (!lastMessage || lastMessage.source_type !== MessageSourceType.ENTRANTE) return null
  const timestamp = lastMessage.date || new Date().toISOString()
  return calculateWaitingTime(timestamp)
}

/**
 * Los chulitos son del ultimo mensaje del chat, no del ultimo saliente: si lo
 * ultimo que se dijo lo dijo el cliente, no hay estado de entrega que mostrar.
 */
const getLastMessageStatus = (chat: Chat): MessageStatus | null => {
  const lastMessage = getLastVisibleMessage(chat.messages)
  if (!lastMessage) return null
  const isOutgoing = [MessageSourceType.SALIENTE, MessageSourceType.TEMPLATE].includes(
    lastMessage.source_type,
  )
  return isOutgoing ? lastMessage.status || null : null
}
</script>

<style scoped>
.filters-slide-enter-active,
.filters-slide-leave-active {
  transition:
    max-height 0.25s ease,
    opacity 0.2s ease,
    transform 0.2s ease;
  overflow: hidden;
}

.filters-slide-enter-from,
.filters-slide-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-4px);
}

.filters-slide-enter-to,
.filters-slide-leave-from {
  max-height: 120px;
  opacity: 1;
  transform: translateY(0);
}
</style>
